import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Slider, TextField, Stack, CssBaseline, createTheme, ThemeProvider, Switch, CircularProgress, InputAdornment, IconButton } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
// removed CanvasJS import
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Calendar } from "lucide-react";

import moment from "moment-timezone";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./Statistics.css";
import { compute_magnitude } from '../recentEqUtils.js';
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";
const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;
// const CanvasJSChart = CanvasJSReact.CanvasJSChart; // removed

const lightTheme = createTheme({
    palette: {
        mode: "light",
        primary: { main: "#ae2e0eff" },
        secondary: { main: "#ae2e0eff" },
        background: { default: "#f5f7fa", paper: "#fff" },
        text: { primary: "#000", secondary: "#444" },
    },
});

const MAGNITUDE_LEGEND_ITEMS = [
  { label: "≥ 6.5", color: "#ff0000" },
  { label: "6.0 - 6.4", color: "#FF7D11" },
  { label: "5.0 - 5.9", color: "#0055BB" },
  { label: "< 5.0", color: "#4C9E00" },
];

const LegendControl = () => {
  return <MapLegendControl sections={[{ title: "Magnitude", shape: "square", items: MAGNITUDE_LEGEND_ITEMS }]} />;
};

export default function Statistics() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [minMag, setMinMag] = useState(2);
    const [dateRange, setDateRange] = useState(() => [
        moment().subtract(1, "month").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD"),
    ]);
    const [search, setSearch] = useState("");
    const [source, setSource] = useState("USGS"); // USGS ↔ RIMES
    const startDateRef = React.useRef(null);
    const endDateRef = React.useRef(null);

    const mapUsgsData = (data) =>
        data.map((e) => ({
            id: e.id,
            title: e.title,
            latitude: parseFloat(e.latitude),
            longitude: parseFloat(e.longitude),
            magnitude: parseFloat(e.magnitude),
            depth: e.depth ? parseFloat(e.depth) : null,
            timeUtc: e.time_in_utc,
        }));

    const mapRimesData = (data) =>
        data.map((e) => ({
            id: e.id,
            title: `M ${e.magnitude} - ${e.region}`,
            latitude: parseFloat(e.latitude),
            longitude: parseFloat(e.longitude),
            magnitude: parseFloat(e.magnitude),
            depth: e.depth ? parseFloat(e.depth) : null,
            timeUtc: e.event_datetime, 
        }));

    useEffect(() => {
        setLoading(true);
        const start = moment(dateRange[0]);
        const end = moment(dateRange[1]);
        if (end.diff(start, "days") > 90) {
            setDateRange([
                end.clone().subtract(30, "days").format("YYYY-MM-DD"),
                dateRange[1],
            ]);
            setLoading(false);
            return;
        }
        const params = new URLSearchParams({
            min_magnitude: minMag,
            start_date: dateRange[0],
            end_date: dateRange[1],
        });

        const endpoint =
            source === "USGS"
            ? `${backendUrl}/EarthQuake/filter_usgs_earthquakes/?${params}`
            : `${backendUrl}/EarthQuake/filter_rimes_earthquakes/?${params}`;

        fetch(endpoint)
            .then((res) => res.json())
            .then((data) => {
                const mapped = source === "USGS" ? mapUsgsData(data) : mapRimesData(data);
                setEvents(mapped);
            })
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, [minMag, dateRange, source]);

    const filteredEvents = useMemo(() => {
        if (!search) return events;
        return events.filter((e) =>
            e.title.toLowerCase().includes(search.toLowerCase())
        );
    }, [events, search]);

    const rawPieData = useMemo(() => {
        const ranges = [
            { label: "2.5 - 3.9", min: 2.5, max: 4.0 },
            { label: "4.0 - 4.9", min: 4.0, max: 5.0 },
            { label: "5.0 - 5.9", min: 5.0, max: 6.0 },
            { label: "6.0 - 6.9", min: 6.0, max: 7.0 },
            { label: "7.0+", min: 7.0, max: 100 },
        ];
        const counts = ranges.map(({ min, max }) =>
            filteredEvents.filter((e) => e.magnitude >= min && e.magnitude < max).length
        );
        return ranges.map(({ label }, i) => ({
            y: counts[i],
            label,
            indexLabel: `${label}: ${counts[i]}`,
            indexLabelFontColor: "white",
            indexLabelPlacement: "inside",
        }));
    }, [filteredEvents]);

    const [pieData, setPieData] = useState(rawPieData);

    useEffect(() => {
        setPieData(rawPieData);
    }, [rawPieData]);

    // toggle handler via synthetic event from Highcharts legend click
    useEffect(() => {
        const handler = (ev) => {
            const clickedLabel = ev.detail;
            setPieData((prevData) =>
                prevData.map((dp) => {
                    if (dp.label === clickedLabel) {
                        return {
                        ...dp,
                        y: dp.y === 0 ? rawPieData.find((r) => r.label === dp.label).y : 0,
                        };
                    }
                    return dp;
                })
            );
        };
        window.addEventListener("pieLegendClick", handler);
        return () => window.removeEventListener("pieLegendClick", handler);
    }, [rawPieData]);

    // compute event counts per day (must be before areaSeries/areaOptions)
    const areaData = useMemo(() => {
        const dateCounts = {};
        let current = moment(dateRange[0]);
        const end = moment(dateRange[1]);
        while (current <= end) {
            dateCounts[current.format("YYYY-MM-DD")] = 0;
            current = current.add(1, "day");
        }
        filteredEvents.forEach((e) => {
            const day = moment(e.timeUtc).format("YYYY-MM-DD");
            if (dateCounts[day] !== undefined) {
                dateCounts[day]++;
            }
        });
        return Object.entries(dateCounts).map(([date, count]) => ({
            x: new Date(date),
            y: count,
            label: moment(date).format("DD MMM YYYY"),
        }));
    }, [filteredEvents, dateRange]);

    // Highcharts series data
    const pieSeriesData = useMemo(() => {
        return pieData.map((p, i) => ({
            name: p.label,
            y: p.y,
            color: ["#4C9E00", "#0055BB", "#FF7D11", "#ff3b3b", "#7f3b8b"][i % 5],
        }));
    }, [pieData]);

    const areaSeriesData = useMemo(() => areaData.map(d => [new Date(d.x).getTime(), d.y]), [areaData]);

    // shared chart font/style
    const commonChartStyle = useMemo(() => ({
        fontFamily: "'Poppins', 'Helvetica', 'IBM Plex Sans,sans-serif'",
        fontSize: "14px",
        color: "#111"
    }), []);

    const pieOptions = useMemo(() => ({
        chart: { type: "pie", style: commonChartStyle },
        title: { text: null },
        tooltip: { style: commonChartStyle, pointFormat: "<b>{point.y}</b>" },
        legend: {
            enabled: true,
            itemStyle: { fontFamily: commonChartStyle.fontFamily, fontSize: commonChartStyle.fontSize, color: commonChartStyle.color },
            labelFormatter: function () {
                // 'this' refers to the legend item (point for pie)
                return `${this.name} : ${this.y}`;
            }
        },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: "pointer",
                // disable labels on slices, show legend only
                dataLabels: {
                    enabled: false
                },
                showInLegend: true,
                // keep monochrome if you want a single-hue palette (optional)
                monochrome: {
                    enabled: true,
                    color: "#125ca6",
                    opacity: 0.85
                },
                center: ["50%", "50%"],
                size: "100%",
                point: {
                    events: {
                        legendItemClick: function () {
                            try { window.dispatchEvent(new CustomEvent("pieLegendClick", { detail: this.name })); } catch (e) {}
                            return false;
                        }
                    }
                }
            }
        },
        // colorByPoint false so monochrome works; set true if you prefer distinct colors
        series: [{ name: "Count", colorByPoint: false, data: pieSeriesData }],
        credits: { enabled: false }
    }), [pieSeriesData, commonChartStyle]);

    const areaOptions = useMemo(() => ({
        chart: { type: "areaspline", style: commonChartStyle }, // changed to areaspline
        title: { text: null },
        xAxis: { 
            type: "datetime",
            labels: { style: { fontFamily: commonChartStyle.fontFamily, fontSize: commonChartStyle.fontSize, color: commonChartStyle.color } }
        },
        yAxis: { 
            title: { text: null },
            labels: { style: { fontFamily: commonChartStyle.fontFamily, fontSize: commonChartStyle.fontSize, color: commonChartStyle.color } }
        },
        tooltip: {
            style: commonChartStyle,
            useHTML: true,
            formatter: function () {
                const dateStr = Highcharts.dateFormat('%e %b %Y', this.x);
                const val = (typeof this.y === 'number') ? this.y : (this.point && this.point.y) || this.y;
                return `${dateStr} : <strong>${val}</strong>`;
            }
        },
        legend: { enabled: false },
        plotOptions: {
            // use areaspline options for smooth filled area
            areaspline: {
                marker: { enabled: false },
                lineWidth: 2,
                fillOpacity: 0.7,
                states: { hover: { lineWidth: 2 } }
            },
            series: {
                dataLabels: { style: { fontFamily: commonChartStyle.fontFamily, fontSize: commonChartStyle.fontSize, color: commonChartStyle.color } },
                enableMouseTracking: true
            }
        },
        // explicit series type areaspline to ensure smooth area
        series: [{ type: 'areaspline', name: '', data: areaSeriesData, color: "#6D78AD" }],
        credits: { enabled: false }
    }), [areaSeriesData, commonChartStyle]);

    const clusterIconCreate = (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
            html: `<div class="custom-cluster-eq-statistics">${count}</div>`,
            className: "custom-cluster-wrapper-eq-statistics",
            iconSize: L.point(40, 40, true),
        });
    };

    const mapCenter =
        filteredEvents.length > 0
            ? [filteredEvents[0].latitude, filteredEvents[0].longitude]
            : [-8.87, 125.72];

    const totalEvents = filteredEvents.length;

    return (
        <ThemeProvider theme={lightTheme}>
            <CssBaseline />
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ 
                    p: 2.5,
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
                    border: "1px solid #e8ecf1",
                    marginBottom: "16px",
                    overflow: "visible",
                    position: "relative",
                    zIndex: 1
                }}
            >
            {/* Source Toggle */}
            <Box display="flex" alignItems="center" gap={1}>
                <Typography sx={{ color: "black", fontWeight: source === "USGS" ? "bold" : "normal" }}> USGS </Typography>
                <Switch
                    checked={source === "RIMES"}
                    onChange={() => setSource(source === "USGS" ? "RIMES" : "USGS")}
                    color="primary"
                    sx={{
                        "& .MuiSwitch-thumb": { backgroundColor: "#ffffff" },
                        "& .MuiSwitch-track": { backgroundColor: "#ff3b3b" } // keeps track blue
                    }}
                />
                <Typography sx={{ color: "black", fontWeight: source === "RIMES" ? "bold" : "normal" }}> RIMES </Typography>
            </Box>
            <TextField
                label="Search Title"
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 200 }}
            />
            <TextField
                inputRef={startDateRef}
                label="Start Date"
                type="date"
                size="small"
                value={dateRange[0]}
                onChange={(e) => setDateRange([e.target.value, dateRange[1]])}
                sx={{ width: 180, position: "relative", zIndex: 10 }}
                InputLabelProps={{
                    shrink: true,
                }}
                inputProps={{
                    style: { cursor: "pointer" }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ pointerEvents: "auto" }}>
                            <IconButton
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    startDateRef.current?.showPicker?.();
                                }}
                                edge="end"
                                size="small"
                                tabIndex={-1}
                                sx={{ cursor: "pointer", color: "#1976d2", pointerEvents: "auto" }}
                            >
                                <Calendar size={18} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <TextField
                inputRef={endDateRef}
                label="End Date"
                type="date"
                size="small"
                value={dateRange[1]}
                onChange={(e) => setDateRange([dateRange[0], e.target.value])}
                sx={{ width: 180, position: "relative", zIndex: 10 }}
                InputLabelProps={{
                    shrink: true,
                }}
                inputProps={{
                    style: { cursor: "pointer" }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ pointerEvents: "auto" }}>
                            <IconButton
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    endDateRef.current?.showPicker?.();
                                }}
                                edge="end"
                                size="small"
                                tabIndex={-1}
                                sx={{ cursor: "pointer", color: "#1976d2", pointerEvents: "auto" }}
                            >
                                <Calendar size={18} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Box sx={{ width: 180, px: 1 }}>
                <Typography gutterBottom>Min Magnitude</Typography>
                <Slider
                    min={2}
                    max={8}
                    step={0.1}
                    value={minMag}
                    onChange={(e, val) => setMinMag(val)}
                    valueLabelDisplay="auto"
                />
            </Box>
        </Stack>

        <Box className="dashboard-container-eq-statistics" sx={{ position: "relative" }}>
        {/* Show loader on top of content */}
        {loading && (
            <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255,255,255,0.6)",
                zIndex: 10,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
            >
            <CircularProgress />
            </Box>
        )}
        {/* Left: Pie chart */}
        <Box className="panel-eq-statistics chart-panel-eq-statistics" sx={{ padding: 2 }}>
            <Typography variant="h6" gutterBottom> Magnitude Distribution </Typography>
            <div style={{ width: "100%", height: "100%" }}>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={pieOptions}
                    containerProps={{ style: { width: "100%", height: "100%" } }}
                />
            </div>
        </Box>

        {/* Middle: Map */}
        <Box className="panel-eq-statistics map-panel-eq-statistics">
          <MapContainer
            center={mapCenter}
            zoom={6}
            style={{ height: "100%", width: "100%" }}
            attributionControl={true}
            zoomControl={false}
          >
            <MapHomeControl />
            <MapInfoControl text="Historical earthquake statistics around the Timor region. Use the filters to explore events by time, magnitude and depth." />
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution={
                    source === "USGS"
                    ? "Map data © USGS"
                    : "Map data © RIMES"
                }
            />
            <LegendControl />
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={clusterIconCreate}
            >
              {filteredEvents.map((e) => { //console.log(e);
                const t = compute_magnitude(parseFloat(e.magnitude));
                return (
                  <Marker
                    key={e.id}
                    position={[e.latitude, e.longitude]}
                    icon={L.divIcon({
                        className: "animated-circle-icon",
                        html: `
                            <div class="circle-container-eq" style="--circle-color: ${ t.color }">
                                <div class="pulse-ring-eq"></div>
                                <div class="circle-center-eq">${e.magnitude.toFixed(1)}</div>
                            </div>
                            `,
                        iconSize: [40, 40],
                        iconAnchor: [20, 20],
                    })}
                    >
                    <Popup className="custom-popup-class">
                        <h2 className="tbTit" variant="subtitle1" color="primary"> {e.title} </h2>
                        {[
                            {
                                label: "Date & Time (UTC):",
                                value: moment.utc(e.timeUtc).format("DD MMM YYYY hh:mm:ss A"),
                            },
                            {
                                label: "Date & Time (TLT):",
                                value: `${moment.utc(e.timeUtc).tz("Asia/Dili").format("DD MMM YYYY hh:mm:ss A")} (TLT)`,
                            },
                            {
                                label: "Longitude :",
                                value: e.longitude ? Number(e.longitude).toFixed(4) : "N/A",
                            },
                            {
                                label: "Latitude :",
                                value: e.latitude ? Number(e.latitude).toFixed(4) : "N/A",
                            },
                            { label: "Magnitude :", value: e.magnitude },
                            {
                                label: "Depth :",
                                value: e.depth ? `${e.depth} km` : "N/A",
                            },
                        ].map(({ label, value }) => (
                            <React.Fragment key={label}>
                            <span style={{ fontSize: "0.7rem" }}>
                                <strong>{label}</strong> {value}
                            </span>
                            <br />
                            </React.Fragment>
                        ))}
                        </Popup>
                    </Marker>
                );
              })}
            </MarkerClusterGroup>
          </MapContainer>
          
        </Box>

        {/* Right: Area chart */}
        <Box className="panel-eq-statistics chart-panel-eq-statistics" sx={{ padding: 2 }}>
          <Typography variant="h6" gutterBottom>
            Events Per Day
          </Typography>
          <div style={{ width: "100%", height: "100%" }}>
            <HighchartsReact
                highcharts={Highcharts}
                options={{
                  ...areaOptions,
                  subtitle: { text: `Total = ${totalEvents}`, align: "right", style: { fontSize: "14px", fontWeight: "600" } }
                }}
                containerProps={{ style: { width: "100%", height: "100%" } }}
            />
          </div>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
